/**
 * Slovak (`sk`) string catalogue — mirrors the key shape of `./en.ts`. Brand
 * names (`OVERVOLT`, `Sniper Rail`, `Arc Coil`, `Cryo Emitter`, `Splash
 * Mortar`, `Pulse Turret`, `Pylon`, `Flux`) remain in English; the rest
 * translates idiomatically into terse, gamey Slovak.
 */

import type { Catalogue } from './en';

export const sk: Catalogue = {
  meta: {
    name: 'Slovenčina',
    code: 'SK',
  },

  towers: {
    pulse: {
      label: 'Pulse Turret',
      short: 'PULZ',
      role: 'Neutrálny generalista. Spoľahlivé poškodenie kdekoľvek na ceste.',
    },
    arc: {
      label: 'Arc Coil',
      short: 'ARC',
      role: 'Napojený na sieť. Silnejší pri prebytku Pylonov, slabší pri výpadku.',
    },
    cryo: {
      label: 'Cryo Emitter',
      short: 'CRYO',
      role: 'Maliar úzkeho hrdla. Malý dosah — spomalí a označí každého nepriateľa v jednom mieste.',
    },
    mortar: {
      label: 'Splash Mortar',
      short: 'MÍNOM.',
      role: 'Nepriama paľba. Postav ho ĎALEJ od cesty — zblízka má mŕtvu zónu.',
    },
    sniper: {
      label: 'Sniper Rail',
      short: 'SNIPER',
      role: 'Priestrel v priamke. Silný na dlhých rovinkách; v rohoch slabý.',
    },
    pylon: {
      label: 'Pylon',
      short: 'PYLON',
      role: 'Zdroj energie. Postav ich dosť na pokrytie spotreby veží (a prebytok pre Arc).',
    },
    wall: {
      label: 'Wall Node',
      short: 'STENA',
      role: 'Lacný blokátor — len v režime Maze. Tvaruje cestu, ktorou musia nepriatelia prejsť.',
    },
  },

  roleHints: {
    pulse: 'Pulse Turret: spoľahlivá voľba — solídne poškodenie kdekoľvek na ceste.',
    arc: 'Arc Coil: rastie s prebytkom energie. Postav viac Pylonov pre silnejšiu reťaz.',
    cryo: 'Cryo Emitter: maľuje úzke hrdlo. Spomalí a označí pár dlaždíc v zúžení.',
    mortar: 'Splash Mortar: nepriama paľba. Postav ho ĎALEJ od cesty — zblízka netrafí.',
    sniper: 'Sniper Rail: priestrel v priamke. Zameraj dlhú rovinku pre maximálny účinok.',
    pylon: 'Power Pylon: kŕmi sieť. Postav dosť zdrojov na pokrytie spotreby veží.',
    wall: 'Stena: lacný blokátor — tvaruje cestu, ktorou musia nepriatelia prejsť.',
  },

  hints: {
    build:
      'Postav vežu: vyber jednu nižšie (alebo stlač 1) a klikni na svietiacu dlaždicu pri ceste.',
    placed: 'Dobre. Pridaj ďalšiu a stlač medzerník na spustenie vlny.',
    wave1: 'Veže strieľajú samé. Strážte, aby ukazovateľ napájania nezašiel do červenej.',
    select: 'Klikni na postavenú vežu, aby si ju vybral — U vylepšiť, S predať.',
    brownout: 'Výpadok! Veže spomalili. Postav Pylon (3) a pridaj napájanie.',
  },

  contextHints: {
    overload:
      'Overload: vytiahni ďalšiu vlnu pre väčšie odmeny — pri vyššom riziku. Pomalý štart? Vytoč ju pre rýchlejší Flux.',
    profileKey: 'Stlač P pre Profil — Flux odomyká nové veže, schopnosti a režimy.',
    endlessMod: 'Endless rotuje MODIFIKÁTORY vĺn — prispôsob zostavu.',
    puzzleHover:
      'Režim Puzzle: ručne tvorené logické úrovne so stenami blokujúcimi výhľad. Úplne iná hra.',
  },

  hud: {
    buildZone: 'STAVBA',
    waveZone: 'VLNA',
    abilitiesZone: 'SCHOPNOSTI',
    overloadZone: 'OVERLOAD',

    startWave: 'SPUSTIŤ VLNU',
    nextWave: 'ĎALŠIA VLNA',
    pause: 'PAUZA',
    resume: 'POKRAČOVAŤ',
    restart: 'RESTART',
    deployHint: 'spustiť [SPACE]',
    holdHint: 'pauza [SPACE]',
    rebootHint: 'reset [R]',
    nextWaveHint: 'ĎALŠIA VLNA',

    audioOn: 'ZVUK ZAP',
    audioOff: 'ZVUK VYP',

    credits: 'KREDITY',
    core: 'JADRO',
    wave: 'VLNA',
    endless: 'ENDLESS',
    score: 'SKÓRE',
    powerGrid: 'ENERGETICKÁ SIEŤ',

    runMode: 'REŽIM',
    difficulty: 'OBTIAŽNOSŤ',
    profile: 'PROFIL',
    map: 'MAPA',
    clickHint: '▷ klik',

    locked: 'UZAMKNUTÉ',
    lockedShort: 'UZAMK.',
    lockedTooltip:
      'UZAMKNUTÉ — odomkni za ✦{cost} Flux v Profile (stlač P).',
    lockedTowerNotice: '{tower} uzamknuté',
    lockedAbility: 'Schopnosť uzamknutá',
    lockedOverload: 'Tier Overload uzamknutý',
    lockedMode: 'Režim {mode} uzamknutý',
    lockedDifficulty: 'Hard uzamknutý',

    cheatPip: '🔓 CHEAT',
    profilePip: '👤 {name}',

    overloadIdle: '— overload sa nastaví od vlny 1 —',
    overloadTier: 'TIER {tier} · {name}',
    overloadActive: '◉ AKTÍVNY {sec}s',

    maxLevel: 'MAX. ÚROVEŇ',
    upgrade: 'VYLEPŠIŤ L{level}',
    sell: 'PREDAŤ',
    target: 'CIEĽ: {mode}',
    cycleTarget: 'prepnúť · [T]',
    sellHint: '+{refund}c · [S]',
    upgradeHint: '{cost}c · [U]',
    overclock: 'OVERCLOCK',
    overclockOn: 'OVERCLOCK ✦ ZAP',

    // Sprint 69 (i18n-gaps-extract-s69) — extracted from render.ts / hud.ts.
    buildHintIntro: "FÁZA STAVBY  //  vyber vežu [1·2·3·4·5], postav ju a SPUSTI VLNU [SPACE]",
    buildHintReinforce: "FÁZA STAVBY  //  posilni sieť a SPUSTI VLNU {wave} [SPACE]",
    mazeRoute: "T R A S A   B L U D I S K A",
    tiles: "POLÍ",
    abilityActive: "◉ AKTÍVNE {sec}s",
    costSuffix: '{cost}c',
    overclockPip: 'OC ×{n}',
    powerReadout: '{demand} / {supply}W  ·  {pct}%',
    towerCostDraw: '{cost}c {power}w',
    pylonCostSupply: '{cost}c +{supply}w',
    wallCost: '{cost}c 0w',
    endlessModBadge: 'MOD · {label}',
    startWaveOverload: '⚡{n} {tierName}',
    waveOverloadPip: '⚡{n} {tierName}',
    overclockNoticeOn: "{tower} Overclock ZAP",
    overclockNoticeOff: "{tower} Overclock VYP",

    tierMult: {
      hp: "HP",
      sp: "RY",
      cn: "PO",
      gd: "ZL",
      sc: "SK",
    },
    spawnShort: {
      bit: "Bit",
      packet: "Pkt",
      daemon: "Dmn",
      fork: "Fork",
      cipher: "Cph",
      patch: "Pch",
      buffer: "Buf",
      streak: "Stk",
      kernel: "KERNEL",
    },
  },

  modes: {
    campaign: 'KAMPAŇ',
    endless: 'ENDLESS',
    maze: 'BLUDISKO',
    puzzle: 'PUZZLE',
  },
  modeSub: {
    campaign: '10 vĺn · vyhrať',
    endless: '✦ {cost}',
    maze: '✦ {cost}',
    puzzle: 'vyber úroveň',
  },
  modeBlurb: {
    campaign: '10 vĺn. Boss na 10. vlne. Vyhraj a postupuješ ďalej.',
    endless: 'Tá istá mapa. Modifikátor každú vlnu. Prispôsob sa alebo presakuje.',
    maze: 'Otvorené pole. Cestu si tvoríš stenami. Spomaľ ich alebo im uberaj korisť.',
    puzzle: '10 ručne skladaných úrovní. Nájdi tú jednu víťaznú zostavu.',
  },
  modeCard: {
    campaign: {
      tagline: 'Nauč sa sieť, zraz bossa.',
      bullet1: '10 ručne ladených vĺn + boss',
      bullet2: 'Pevná S-trasa — stav, vlna, vylepši',
      bullet3: 'Výhra odomyká Endless na tejto mape',
      length: '~8 min',
      skill: 'Trénuje poradie stavby',
    },
    endless: {
      tagline: 'Prispôsob sa alebo presakuje. Konca niet.',
      bullet1: 'Hrá Kampaň 1–10, potom rastie navždy',
      bullet2: 'Nový modifikátor každú vlnu',
      bullet3: 'Koniec, keď jadro padne',
      length: '~30 min',
      skill: 'Trénuje dlhé pivoty',
    },
    maze: {
      tagline: 'Staviaš bludisko, ktorým musia prejsť.',
      bullet1: 'Otvorené pole — bez nakreslenej cesty',
      bullet2: 'Veže a lacné steny tvoria trasu',
      bullet3: 'Dlhšia trasa = viac času v paľbe',
      length: '~12 min',
      skill: 'Trénuje smerovanie priestoru',
    },
    puzzle: {
      tagline: 'Jedna zostava vyhráva. Nájdi ju.',
      bullet1: '10 úrovní, jediné riešenie',
      bullet2: 'Pevný rozpočet a pevná vlna',
      bullet3: 'Steny blokujúce výhľad + limity veží',
      length: 'po ~2 min',
      skill: 'Trénuje dedukciu',
    },
  },
  difficulty: {
    easy: 'ĽAHKÁ',
    normal: 'NORMÁLNA',
    hard: 'ŤAŽKÁ',
  },
  difficultySub: {
    easy: 'mierna',
    normal: 'vyvážená',
    hard: '✦ {cost}',
  },

  overlay: {
    paused: 'PAUZA',
    pauseHint: 'STLAČ  MEDZERNÍK  PRE  POKRAČOVANIE',

    missionComplete: '// MISIA SPLNENÁ',
    gridSecured: 'SIEŤ ZABEZPEČENÁ',
    wavesRepelled: 'Všetkých {total} útočných vĺn odrazených.',
    continueEndless: 'POKRAČOVAŤ  →  ENDLESS',
    unlockEndless: 'odomkni Endless v Profile pre pokračovanie',
    restartCampaign: 'alebo stlač  R  pre reštart kampane',

    systemFailure: '// ZLYHANIE SYSTÉMU',
    coreBreached: 'JADRO PRELOMENÉ',
    endlessEnded: 'Endless beh skončil vo vlne {wave}.',
    reactorOverrun: 'Reaktor premožený vo vlne {wave} z {total}.',
    runItBack: 'STLAČ  R  PRE  NOVÝ  POKUS',

    finalScore: 'FINÁLNE SKÓRE',
    newHighScore: '★  NOVÉ NAJVYŠŠIE SKÓRE  ★',
    newEndlessBest: '★  NOVÉ ENDLESS MAXIMUM  ★',
    highScore: 'NAJVYŠŠIE SKÓRE',
    endlessBest: 'ENDLESS MAXIMUM',

    // Sprint 69 — top-banner countdown overlays drawn by render.ts.
    aftershock: "// DOZNIEVANIE — DAŇ SIETI  {sec}s //",
    overcharge: "// OVERCHARGE — SIEŤ POSILNENÁ  {sec}s //",
    brownout: "// VÝPADOK SIETE — VEŽE SPOMALENÉ //",
  },

  profile: {
    title: 'PROFIL',
    subtitle: '// META-PROGRES',
    closeHint: 'stlač  P  alebo  ESC  pre zatvorenie',
    tabUnlocks: 'ODOMKNUTIA',
    tabScoreboard: 'TABUĽKA',
    flux: 'FLUX',
    unlockTree: 'STROM ODOMKNUTÍ',
    unlockedCount: '{owned} / {total} ODOMKNUTÉ',
    lifetime: 'CELKOM',

    branchArsenal: 'ARZENÁL',
    branchModes: 'REŽIMY',
    branchChallenge: 'VÝZVA',
    branchCosmetic: 'KOZMETIKA',

    statRunsPlayed: 'POČET BEHOV',
    statCampaignWins: 'VÝHRY KAMPANE',
    statBestScore: 'NAJVYŠŠIE SKÓRE',
    statDeepestEndless: 'NAJHLBŠÍ ENDLESS',
    statTotalFlux: 'CELKOM FLUX',

    personalBests: 'OSOBNÉ REKORDY',
    waveLabel: 'VLNA {wave}',

    runHistory: 'HISTÓRIA BEHOV',
    noRuns: 'Zatiaľ žiadne behy — dokonči jeden a začni zbierať.',
    earlierRuns: '+ {n} starší beh',
    earlierRunsPlural: '+ {n} starších behov',
    colRun: 'BEH',
    colMode: 'REŽIM · OBTIAŽNOSŤ',
    colResult: 'VÝSLEDOK',
    colOverload: 'OVL',
    colScore: 'SKÓRE',
    colDeltaBest: 'Δ NAJL.',
    colFlux: 'FLUX',
    starBest: '★ NAJLEPŠÍ',

    statusUnlocked: 'ODOMKNUTÉ',
    statusOwned: 'VLASTNENÉ',
    statusRequiresPrereq: 'Najprv {prereq}',
    statusFluxShort: 'Chýba ✦{short} Flux',
    statusAffordable: 'Dostupné — klikni pre odomknutie',
    statusCost: '✦{cost} pre odomknutie',
    buyAction: 'KÚPIŤ  ▸',
    nodeReq: 'POŽAD: {prereq}',

    profileLauncher: 'PROFIL',
    profileKeyHint: '[ P ]',
    viewScoreboard: 'ZOBRAZIŤ  TABUĽKU',
    newPersonalBest: '★  NOVÝ OSOBNÝ REKORD  ★',
    fromYourBest: 'OD TVOJHO MAXIMA',

    grantTower: 'ÚTOČNÁ VEŽA',
    grantAbility: 'AKTÍVNA SCHOPNOSŤ',
    grantMode: 'HERNÝ REŽIM',
    grantDifficulty: 'OBTIAŽNOSŤ',
    grantOverload: 'OVERLOAD TIER {tier}',
    grantTheme: 'TÉMA DOSKY',

    // Sprint 69 — unlock-tree hover preview.
    nodePreview: {
      towerMortar: "Nepriama paľba. Postav ho ĎALEJ od cesty — zblízka mŕtva zóna; granáty padajú na zhluky a drvia brnenie.",
      towerSniper: "Lanč v priamke. Zameraj dlhú rovinku — každý nepriateľ v línii dostane; v rohoch slabý.",
      abilityAirstrike: "Jednorázový výbuch na cieľové pole (45c). Vyčisti zhluk alebo dorob silného nepriateľa.",
      abilityEmp: "Plošný stun (80c). Zmraz na chvíľu všetkých nepriateľov, keď je línia premožená.",
      modeEndless: "Hraj za vlnou 10 — procedurálne vlny pokračujú, kým Jadro nepadne. Nová honba za skóre.",
      modeMaze: "Otvorené pole — veže blokujú polia, nepriatelia hľadajú cestu okolo (A*).",
      difficultyHard: "Tvrdšie vlny; väčšie násobiče zlata a skóre. Odomyká horné pásmo Flux.",
      overloadCritical: "Overload tier 3 — vytoč to a získaj tučnejšie násobiče skóre/Flux v ďalšej vlne.",
      overloadMeltdown: "Overload tier 4 — najťažšia pevná vlna. Špičkové násobiče; len pre stabilný build.",
      themeAmber: "Kozmetická paleta — teplý jantárový CRT terminál. Čisto kozmetické; bez vplyvu.",
      themePhosphor: "Kozmetická paleta — starý zelený fosfor. Čisto kozmetické; bez vplyvu.",
    },
  },

  help: {
    eyebrow: '/ /  R E F E R E N C I A',
    title: 'PREVÁDZKOVÁ PRÍRUČKA',
    controls: 'OVLÁDANIE',
    powerGrid: 'ENERGETICKÁ SIEŤ',
    powerGridBody:
      'Zbrane berú energiu; Pylony ju dodávajú. Keď spotreba prerastie dodávku, sieť padne do výpadku a všetky veže spomalia — postav Pylony, nech strieľajú ďalej.',
    objective: 'CIEĽ',
    objectiveBody:
      'Vyčisti všetkých 15 vĺn kampane a vyhrávaš; prehrávaš v okamihu, keď životy Jadra padnú na nulu. Endless beží naveky.',
    mazeMode: 'REŽIM BLUDISKO',
    mazeModeBody:
      'Tretí herný režim na otvorenom poli: veže blokujú dlaždice, na ktorých stoja, a nepriatelia hľadajú cestu okolo (A*), takže staviaš bludisko. Jadro nemôžeš úplne zamurovať.',
    glitches: 'GLITCHE',
    towers: 'VEŽE',
    abilities: 'SCHOPNOSTI',
    replayIntro: '↺  ZNOVA INTRO',
    closeHint: 'Esc / H / ? alebo klik kdekoľvek zatvorí',
  },

  intro: {
    eyebrow: '/ /  S Y S T É M O V Ý   B R I E F I N G',
    titleBig: 'OVERVOLT',
    subtitle: 'Ubráň Jadro',
    line1:
      'Poškodené programy sa valia obvodom k Jadru. Postav veže a znič ich skôr, než prelomia obranu.',
    line2:
      '⚡ Každá veža berie energiu. Ak postavíš priveľa, sieť padne — všetky veže naraz spomalia. Stavaj Pylony, nech energia tečie.',
    line3: 'Stavaj v pokojnej fáze, potom stlač Spustiť vlnu. Prežiješ 15 vĺn a vyhrávaš.',
    line4: 'Myš stavia · 1–5 výber · Medzerník štart/pauza · U vylepšiť · S predať',
    gotIt: 'JASNÉ  →',
    gotItHint: 'stlač ľubovoľný kláves',
  },

  puzzle: {
    label: 'PUZZLE',
    levels: {
      'p0-first-light': {
        name: 'Prvé Svetlo',
        hint: 'Postav Pulse vedľa koridoru. Stačí akákoľvek dlaždica v dosahu.',
      },
      'p1-long-lance': {
        name: 'Dlhá Kopija',
        hint: 'Jeden Sniper zarovnaný s líniou prestrelí každého nepriateľa.',
      },
      'p2-over-the-wall': {
        name: 'Cez Stenu',
        hint: 'Stena blokuje každý priamy lúč. Granáty z mínometu preletia oblúkom.',
      },
      'p3-funhouse-mirror': {
        name: 'Krivé Zrkadlo',
        hint: 'Sniper prevlečený medzerou zasiahne obe línie.',
      },
      'p4-surplus-riches': {
        name: 'Prebytok Bohatstva',
        hint: 'Postav Pylony cez voľné pásmo. Arc Coil žije z prebytku.',
      },
      'p5-pinch-point': {
        name: 'Zovretie',
        hint: 'Cryo namaľuje zúženie; Pulses naokolo dokončia hromadu.',
      },
      'p6-cipher-gauntlet': {
        name: 'Cipher Rukavica',
        hint: 'Mínomet drví brnenie; Cryo maľuje ohyb; Sniper prestrelí spodné rameno. Použi celú zostavu.',
      },
      'p7-kernel-echo': {
        name: 'Ozvena Kernelu',
        hint: 'Mínomety lobujú cez steny výhľadu. Sniper preprevedie zospodu. Sleduj Power Drain.',
      },
      'p8-overclock-crunch': {
        name: 'Overclock Tieseň',
        hint: 'Jeden Pulse nestihne. Overclockuj v rozhodujúcej chvíli — priemerná rýchlosť nestačí.',
      },
      'p9-pylon-pinch': {
        name: 'Pylónové Zovretie',
        hint: 'Zovri Pylony medzi veže — aura odmení tesnú stavbu.',
      },
    },
    briefing: {
      eyebrow: "BRIEFING PUZZLE",
      startBtn: "Spustiť ▷",
      cancelBtn: "Zrušiť",
      sectionConstraints: "OBMEDZENIA",
      sectionTowers: "VEŽE",
      sectionWinCondition: "PODMIENKA VÍŤAZSTVA",
      budgetLabel: "Rozpočet zlata",
      wavesLabel: "Vlny",
      towerAllowed: "POVOLENÉ",
      towerForbidden: "ZAKÁZANÉ",
      resultEyebrowWon: "PUZZLE VYRIEŠENÉ",
      resultEyebrowLost: "PUZZLE NEDOKONČENÉ",
      resultTitleWon: "VYRIEŠENÉ!",
      resultTitleLost: "SKÚSIŤ ZNOVA",
      resultStatTowers: "VEŽE",
      resultStatGold: "MINUTÉ ZLATO",
      resultStatLives: "ZOSTÁVA ŽIVOTOV",
      resultNextBtn: "Ďalšia úroveň ▷",
      resultReplayBtn: "Prehrať znova",
      resultResetBtn: "Reštartovať pokus",
      resultBackBtn: "Späť na výber",
    },

    // Sprint 69 — puzzle level-select overlay chrome.
    select: {
      subtitle: "Ručne tvorené logické úrovne — odvoď tú jednu víťaznú zostavu.",
      close: "Zavrieť",
      cardTitle: 'P{index}  {name}',
      lockedCard: "🔒 ZAMKNUTÉ",
      solved: "✓ Vyriešené",
      unsolved: "▷ Nevyriešené",
      noSolve: "Zatiaľ nevyriešené",
      bestLine: "Najlepšie: {towers}v · {gold}z · {power}p",
      allowed: "Povolené: {list}",
      parPlaceholder: "par: — / — / —",
      parRow: "{towers}v · {gold}z · {power}p",
    },
  },

  localeChip:
 {
    label: 'JAZYK',
    en: 'EN',
    cs: 'CS',
  },

  resume: {
    title: 'Pokračovať v predchádzajúcom behu?',
    resume: 'Pokračovať',
    newRun: 'Nový beh',
    hint: '[Enter] Pokračovať   ·   [Esc] Nový',
    detail: "{mode} · {difficulty} · vlna {wave} · {towers} veží · {gold}g · {lives}♥",
  },

  // -------- Sprint 69: Kernel boss UI chrome ---------------------------------
  boss: {
    kernelHeader: "⚠  KERNEL",
    phase: "FÁZA {phase} · {name}",
    powerDrain: "⚡ ODČERPANIE",
    kernelIncoming: "⚠  KERNEL  PRICHÁDZA  ⚠",
    phaseName: {
      boot: "BOOT SEQUENCE",
      fork: "FORK BOMB",
      panic: "KERNEL PANIC",
    },
  },

  // -------- Sprint 69: Map registry display strings --------------------------
  maps: {
    default: { label: "Datawash", tagline: "Otvorený S-ohyb — klasický OVERVOLT layout." },
    cramped: { label: "Switchyard", tagline: "Stiesnené koridory — každé pole sa počíta." },
    lines: { label: "Long Lines", tagline: "Dlhé rovinky — preveď lanč." },
    serpentine: { label: "Snake Run", tagline: "Úzky had — Cryo painter žiari." },
  },

  // -------- Sprint 69: Ability display names ---------------------------------
  abilities: {
    overcharge: { label: 'Overcharge' },
    airstrike: { label: 'Airstrike' },
    empBurst: { label: 'EMP Burst' },
  },

  // -------- Sprint 69: Overload tier names -----------------------------------
  overload: {
    tierName: {
      nominal: "Nominálny",
      charged: "Nabitý",
      surging: "Stúpajúci",
      critical: "Kritický",
      meltdown: "Meltdown",
    },
  },

  // -------- Sprint 69: Endless modifier labels -------------------------------
  endlessMod: {
    armoredSurge: { label: "Obrnený nápor" },
    fastPack: { label: "Rýchla svorka" },
    scatteredSpread: { label: "Rozptyl" },
    streakRunners: { label: "Streak bežci" },
    daemonRush: { label: "Daemonský útok" },
  },

  // -------- Sprint 69: Enemy display names (Help roster) ---------------------
  enemies: {
    bit: { label: "Bit" },
    packet: { label: "Packet" },
    daemon: { label: "Daemon" },
    fork: { label: "Fork" },
    cipher: { label: "Cipher" },
    patch: { label: "Patch" },
    buffer: { label: "Buffer" },
    streak: { label: "Streak" },
    kernel: { label: "Kernel" },
  },

  // -------- Sprint 69: Help-panel enemy one-liners ---------------------------
  enemyRole: {
    bit: "Základné krmenie — lacný, pomalý, krehký.",
    packet: "Rýchly a slabý; ženie sa k Jadru.",
    daemon: "Ťažká HP špongia; po prielome silno bije.",
    fork: "Pri zničení sa rozdelí na dva Bity.",
    cipher: "Obrnený — rozdrť Mínometom.",
    patch: "Lieči okolitých Glitchov; zlož ho prvý.",
    buffer: "Regeneračný štít; preraz burstom.",
    streak: "Sám šprintuje; odoláva slowu. Pokry cestu.",
    kernel: "Fázový boss — odčerpáva energiu. Zlož pred ďalšou fázou.",
  },

  // -------- Sprint 69: Help-panel ability one-liners -------------------------
  abilityRole: {
    overcharge: "Posilni sieť: každá veža strieľa krátko rýchlejšie. Naplánuj okolo ťažkej vlny.",
    airstrike: "Jednorázový výbuch na cieľové pole. Vyčisti zhluk alebo dorob silného nepriateľa.",
    empBurst: "Plošný stun — všetci nepriatelia chvíľu zamrznú. Kúp si čas, keď je línia premožená.",
  },

  // -------- Sprint 69: Tooltip chrome ----------------------------------------
  tooltip: {
    levelOf: "LV {level}/{max}",
    credits: "{cost} kreditov",
    next: "ĎALEJ  →  LV {level}   {cost} kreditov",
    cooldown: "COOLDOWN  {sec}s",
    stat: {
      supply: "DODÁVKA  +{n}w do siete",
      dmgRngCd: "DMG {damage}    RNG {range}    CD {cd}s",
      pwrChain: "PWR {power}w    REŤAZ {chain}",
      pwrAoe: "PWR {power}w    AOE {targets} cieľov",
      slow: "SLOW ×{factor}  na  {sec}s",
      brittle: "KREHKOSŤ +{pct}% dmg od ostatných veží",
      pwrBlast: "PWR {power}w    BLAST {tiles} polí",
      pwrSingle: "PWR {power}w    1 cieľ",
    },
  },

  // -------- Sprint 69: Help-panel control-row labels -------------------------
  controls: {
    arm: "Vyber Pulse / Arc / Pylon / Cryo / Mínomet / Sniper",
    mouse: "Postav na pole · klikni na vežu na výber",
    space: "Spustiť vlnu · pauza vlny",
    fast: "Zrýchlenie (1× ⇄ 2×)",
    upgradeSell: "Vylepšiť / predať vybranú vežu",
    target: "Prepnúť cieľový režim vybranej veže",
    profile: "Otvoriť Profil (strom odomknutí · skóre)",
    utility: "Zrušiť · stíšiť · reštart behu",
  },

  // -------- Sprint 69: Unlock-tree node display names ------------------------
  unlocks: {
    towerMortar: { name: "Splash Mortar" },
    towerSniper: { name: "Sniper Rail" },
    abilityAirstrike: { name: "Airstrike" },
    abilityEmp: { name: "EMP Burst" },
    modeEndless: { name: "Režim Endless" },
    modeMaze: { name: "Režim Bludisko" },
    difficultyHard: { name: "Obtiažnosť Hard" },
    overloadCritical: { name: "Overload: Critical" },
    overloadMeltdown: { name: "Overload: Meltdown" },
    themeAmber: { name: "Amber Terminál" },
    themePhosphor: { name: "Zelený fosfor" },
  },

  // Sprint 73 — interaktívny tutoriál (docs/TUTORIAL_DESIGN.md §6).
  tutorial: {
    button: {
      start: 'SPUSTIŤ TUTORIÁL',
      startSub: 'Prvýkrát? Prejdite si prehliadku →',
      replay: 'PREHRAŤ ZNOVU',
      replaySub: 'Osviežiť základy',
    },
    step: {
      welcome: {
        title: 'Vitajte v OVERVOLT',
        body: 'Ste obranná AI zlyhávajúceho reaktora. Porušené programy pochodujú obvodom k vášmu Core — zničte ich skôr, než sa prelomia.',
      },
      path: {
        title: 'Cesta',
        body: 'Nepriatelia vždy idú po tejto S-krivke, zo spawn dlaždice vľavo až k Core vpravo. Vy rozhodujete, kam pozdĺž nej postavíte veže.',
      },
      firstPlace: {
        title: 'Postavte prvú vežu',
        body: 'Pulse Turret stojí 50 kreditov a ťahá 4 W. Kliknite na svietiacu dlaždicu vedľa cesty.',
      },
      wave1: {
        title: 'Spustite prvú vlnu',
        body: 'Stlačte SPUSTIŤ VLNU. Vaša Pulse strieľa automaticky na čokoľvek v dosahu.',
      },
      towerRoles: {
        title: 'Päť veží, päť rolí',
        body: 'Pulse: spoľahlivá všade. Sniper: dlhé rovinky. Mortar: nepriama paľba, DALEJ od cesty. Arc: rastie s prebytkom energie. Cryo: maľuje jedno hrdlo.',
      },
      powerGrid: {
        title: 'Energetická sieť',
        body: 'Každá veža ťahá energiu. Keď dopyt prerastá ponuku, sieť padá do výpadku a každá veža spomalí. Stavajte Pylóny — susedné Pylóny zvyšujú rýchlosť paľby.',
      },
      overloadDial: {
        title: 'Overload — riziko vs odmena',
        body: 'Vytočte kotúč hore pre ďalšiu vlnu: tvrdší nepriatelia, väčší Flux. Stiahnite ho dole pre pokoj.',
      },
      overclock: {
        title: 'Overclock veže',
        body: 'Zapnite OVERCLOCK na vybranej veži pre burst okno: rýchlejšia paľba, vyšší odber. Burstová výhra, dlhodobá strata.',
      },
      abilities: {
        title: 'Aktívne schopnosti',
        body: 'Tri sloty: Overcharge nakopne celú sieť. Airstrike zhodí trvalú zónu spálenia. EMP Burst omráči každého. Plánujte ich — nestláčajte panicky.',
      },
      mazePreview: {
        title: 'Maze režim',
        body: 'Žiadna pevná cesta — veže blokujú dlaždice a nepriatelia A*-routujú okolo nich. Vy staviate cestu, ktorou musia prejsť.',
      },
      puzzlePreview: {
        title: 'Puzzle režim',
        body: 'Ručne robené logické levely so stenami línie pohľadu. Pevný rozpočet, pevná vlna — nájdite jediné víťazné rozloženie. Iná hra.',
      },
      wrapUp: {
        title: 'Ste pripravení',
        body: 'Behy získavajú FLUX — utraťte ho v Profile pre odomknutie nových veží, schopností a režimov. Začnite s Kampaňou; vráťte sa pre Endless, Maze a Puzzle.',
      },
    },
    ui: {
      eyebrow: 'KROK {n} / {total}',
      next: 'ĎALEJ →',
      back: '← SPÄŤ',
      skip: 'PRESKOČIŤ',
      play: 'HRAŤ  ▸',
      skipConfirm: 'Preskočiť prehliadku? Môžete ju kedykoľvek prehrať z titulky.',
    },
  },
};
